import type { AgentCommand } from '../../lib/api/admin.ts'
import {
  commandStatePresentation,
  failureText,
  isAwaitingAgent,
  releaseOutcome,
} from '../../lib/commands/semantics.ts'
import { formatDayClock } from '../../lib/format.ts'
import { Badge } from '../primitives/Badge.tsx'
import type { Tone } from '../primitives/Metric.tsx'

/** Estado do comando pelo `state` — nunca pelo `status` da fila. */
export function CommandStateBadge({ state }: { state: string }) {
  const p = commandStatePresentation(state)
  return (
    <Badge tone={p.tone} dot>
      {p.label}
    </Badge>
  )
}

/**
 * O que se pode afirmar sobre a rede da máquina a partir de UM comando de
 * desbloqueio. Puramente apresentacional, para o render check: "liberada" só
 * aparece em `executed`/`skipped` (lib/commands/semantics.ts).
 */
export function ReleaseStatus({
  command,
  outcome: given,
}: {
  command: AgentCommand
  /** Desfecho já redigido por quem acompanha outro tipo de comando (IP, p. ex.). */
  outcome?: { tone: Tone; headline: string }
}) {
  const outcome = given ?? releaseOutcome(command.state)
  const failure = failureText(command)
  const border: Record<string, string> = {
    ok: 'border-ok',
    crit: 'border-crit',
    warn: 'border-warn',
    accent: 'border-accent',
    neutral: 'border-line-strong',
  }

  return (
    <div className={`border-l-2 ${border[outcome.tone]} bg-inset px-3 py-2`} role="status" aria-live="polite">
      <div className="flex flex-wrap items-center gap-2">
        <CommandStateBadge state={command.state} />
        {isAwaitingAgent(command.state) && (
          <span className="text-micro text-fg-muted">acompanhando</span>
        )}
      </div>
      <p className="mt-1.5 text-sm leading-snug text-fg">{outcome.headline}</p>

      {command.state === 'failed' && failure !== null && (
        <p className="mt-1 border-l-2 border-crit px-1.5 font-mono text-micro break-words text-fg-secondary">
          {failure}
        </p>
      )}
      {command.state !== 'failed' && command.result?.detail != null && (
        <p className="mt-1 text-micro leading-snug break-words text-fg-secondary">{command.result.detail}</p>
      )}

      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-micro text-fg-muted">
        <dt>pedido</dt>
        <dd className="tnum font-mono text-fg-secondary">{formatDayClock(command.requested_at)}</dd>
        {command.dispatched_at !== null && (
          <>
            <dt>entregue</dt>
            <dd className="tnum font-mono text-fg-secondary">{formatDayClock(command.dispatched_at)}</dd>
          </>
        )}
        {command.result !== null && (
          <>
            <dt>agente terminou</dt>
            <dd className="tnum font-mono text-fg-secondary">{formatDayClock(command.result.finished_at)}</dd>
          </>
        )}
        {isAwaitingAgent(command.state) && command.expires_at !== null && (
          <>
            <dt>vence</dt>
            <dd className="tnum font-mono text-fg-secondary">{formatDayClock(command.expires_at)}</dd>
          </>
        )}
        {command.attempt_count > 1 && (
          <>
            <dt>entregas</dt>
            <dd className="tnum font-mono text-fg-secondary">{command.attempt_count}</dd>
          </>
        )}
      </dl>
    </div>
  )
}
